import { Close, UploadFile } from '@mui/icons-material';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Modal,
  TextField,
  Typography,
} from '@mui/material';
import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { useSnackBar } from '../../../hooks/useSnackBar';
import { useTranslation } from '../../../hooks/useTranslation';
import {
  BulkInviteResult,
  BulkInviteSucceededItem,
  inviteUsers,
} from '../../../services/inviteService';
import Button from '../../shared/Button/Button';

export const MAX_BULK_INVITE_EMAILS = 500;

const EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export const isValidEmailFormat = (email: string): boolean =>
  EMAIL_REGEX.test(email);

export const splitEmails = (raw: string): string[] =>
  raw
    .split(/[\s,]+/)
    .map(email => email.trim())
    .filter(Boolean);

/**
 * Reads only the first column of a CSV, skipping a header row if the first
 * row's first cell isn't itself a valid email (so both "Email\njane@..." and
 * a bare "jane@..." list, one per line, are accepted). See spec Open
 * Question 5: real HR-export column layouts vary, so this intentionally
 * does not try to detect an "Email" column by name — the admin is expected
 * to hand this a single email column, not a raw multi-column export.
 */
export const parseCsvEmails = (csvText: string): string[] => {
  const workbook = XLSX.read(csvText, { type: 'string' });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows: string[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });
  const nonEmptyRows = rows.filter(row =>
    row.some(cell => String(cell ?? '').trim() !== '')
  );
  if (nonEmptyRows.length === 0) return [];

  const firstCell = String(nonEmptyRows[0][0] ?? '').trim();
  const dataRows = isValidEmailFormat(firstCell)
    ? nonEmptyRows
    : nonEmptyRows.slice(1);

  return dataRows.map(row => String(row[0] ?? '').trim()).filter(Boolean);
};

interface BulkInviteModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (succeeded: BulkInviteSucceededItem[]) => void;
}

const BulkInviteModal: React.FC<BulkInviteModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const showSnackbar = useSnackBar();
  const [rawText, setRawText] = useState('');
  const [emails, setEmails] = useState<string[]>([]);
  const [capError, setCapError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<BulkInviteResult | null>(null);
  const [showFailures, setShowFailures] = useState(false);
  const [showAlreadyInvited, setShowAlreadyInvited] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addEmails = (parsed: string[]) => {
    if (parsed.length === 0) return;
    setEmails(prev => {
      const seen = new Set(prev.map(email => email.toLowerCase()));
      const next = [...prev];
      parsed.forEach(email => {
        const key = email.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          next.push(email);
        }
      });
      if (next.length > MAX_BULK_INVITE_EMAILS) {
        setCapError(
          t('Cannot add more than {{max}} emails in one batch.', {
            max: MAX_BULK_INVITE_EMAILS,
          })
        );
        return prev;
      }
      setCapError(null);
      return next;
    });
  };

  const addParsedEmails = () => {
    addEmails(splitEmails(rawText));
    setRawText('');
  };

  const handleCsvChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!selected) return;

    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result as string;
      addEmails(parseCsvEmails(text));
    };
    reader.readAsText(selected);
  };

  const handleRemove = (email: string) => {
    setEmails(prev => prev.filter(existing => existing !== email));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await inviteUsers(emails);
      setResult(response);
      setShowFailures(false);
      setShowAlreadyInvited(false);
      const handledEmails = new Set(
        [...response.succeeded, ...response.already_invited].map(item =>
          item.email.toLowerCase()
        )
      );
      if (handledEmails.size > 0) {
        setEmails(prev =>
          prev.filter(email => !handledEmails.has(email.toLowerCase()))
        );
      }
      if (response.succeeded.length > 0) {
        onSuccess?.(response.succeeded);
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : t('Failed to send invites.');
      showSnackbar(message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setRawText('');
    setEmails([]);
    setCapError(null);
    setResult(null);
    setShowFailures(false);
    setShowAlreadyInvited(false);
    onClose();
  };

  const validCount = emails.filter(isValidEmailFormat).length;
  const invalidCount = emails.length - validCount;

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 560, md: 640 },
          maxHeight: '85vh',
          bgcolor: 'background.paper',
          borderRadius: '4px',
          boxShadow: 24,
          p: '2rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          overflow: 'auto',
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {t('Invite New Users')}
        </Typography>

        <TextField
          label={t('Email addresses')}
          placeholder={t(
            'Paste email addresses, separated by commas, spaces, or new lines.'
          )}
          multiline
          minRows={4}
          fullWidth
          value={rawText}
          onChange={e => setRawText(e.target.value)}
        />

        <Box sx={{ display: 'flex', gap: '0.75rem' }}>
          <Button
            variant="outlined"
            onClick={addParsedEmails}
            disabled={!rawText.trim()}
          >
            {t('Add Emails')}
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => fileInputRef.current?.click()}
            disabled={isSubmitting}
          >
            <UploadFile sx={{ mr: '0.5rem' }} fontSize="small" />
            {t('Upload CSV')}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={handleCsvChange}
            disabled={isSubmitting}
          />
        </Box>

        {capError && <Alert severity="error">{capError}</Alert>}

        {emails.length > 0 ? (
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: '0.5rem' }}
            >
              {t('{{valid}} valid · {{invalid}} invalid', {
                valid: validCount,
                invalid: invalidCount,
              })}
            </Typography>
            <List
              dense
              sx={{
                maxHeight: 240,
                overflow: 'auto',
                border: '1px solid rgba(15, 23, 42, 0.12)',
                borderRadius: '4px',
              }}
            >
              {emails.map(email => (
                <ListItem
                  key={email}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      size="small"
                      aria-label={t('Remove {{email}}', { email })}
                      onClick={() => handleRemove(email)}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={email}
                    primaryTypographyProps={{
                      color: isValidEmailFormat(email)
                        ? 'text.primary'
                        : 'error',
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('No emails added yet.')}
          </Typography>
        )}

        {result && (
          <Alert severity={result.failed.length > 0 ? 'warning' : 'success'}>
            {t(
              '{{succeeded}} invites sent · {{already_invited}} already invited · {{failed}} failed',
              {
                succeeded: result.succeeded.length,
                already_invited: result.already_invited.length,
                failed: result.failed.length,
              }
            )}
            {result.already_invited.length > 0 && (
              <Box sx={{ mt: '0.5rem' }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => setShowAlreadyInvited(show => !show)}
                >
                  {showAlreadyInvited
                    ? t('Hide already invited')
                    : t('Show already invited ({{count}})', {
                        count: result.already_invited.length,
                      })}
                </Link>
                {showAlreadyInvited && (
                  <List dense>
                    {result.already_invited.map(item => (
                      <ListItem key={item.email} disableGutters>
                        <ListItemText primary={item.email} />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            )}
            {result.failed.length > 0 && (
              <Box sx={{ mt: '0.5rem' }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => setShowFailures(show => !show)}
                >
                  {showFailures
                    ? t('Hide failures')
                    : t('Show failures ({{count}})', {
                        count: result.failed.length,
                      })}
                </Link>
                {showFailures && (
                  <List dense>
                    {result.failed.map(item => (
                      <ListItem key={item.email} disableGutters>
                        <ListItemText
                          primary={`${item.email} — ${item.reason}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            )}
          </Alert>
        )}

        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}
        >
          <Button variant="outlined" color="inherit" onClick={handleClose}>
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={emails.length === 0 || isSubmitting}
          >
            {isSubmitting && (
              <CircularProgress size={16} sx={{ mr: '0.5rem' }} />
            )}
            {t('Send Invites')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default BulkInviteModal;
